/**
 * Pin (Favorites) domain types.
 *
 * A "pin" is a personal, per-user favorite pointing at a single entity the
 * user wants to re-reach quickly. It carries a *snapshot* of just enough
 * metadata to render a rail row and route on click — deliberately decoupled
 * from each feature's own data layer so the shell never has to import feature
 * code. (Snapshot can go slightly stale on rename; acceptable for the proto,
 * swappable for a live-resolve later.)
 */

/** The five pinnable entity kinds — mirrors `SearchObject` (the ⌘K model). */
export type PinKind = 'agent' | 'mcp' | 'policy' | 'template' | 'person';

/** The second-rail sections a pin can surface in. */
export type PinSection = 'inventory' | 'policies' | 'team';

export type PinnedItem = {
  kind: PinKind;
  /** Stable id within the kind (used with `kind` as the composite key). */
  id: string;
  /** Primary line shown in the rail row. */
  label: string;
  /** Optional secondary line (status, host, role, …). */
  sublabel?: string;
  /** Route the pinned row links to. */
  href: string;
  /** Epoch ms when pinned — newest-first ordering in the rail. */
  pinnedAt: number;
};

/** What you pass to `pin()` — `pinnedAt` is stamped by the store. */
export type PinInput = Omit<PinnedItem, 'pinnedAt'>;

/** Which section each kind surfaces in (per the agreed kind→section map). */
export const SECTION_FOR_KIND: Record<PinKind, PinSection> = {
  agent: 'inventory',
  mcp: 'inventory',
  policy: 'policies',
  template: 'policies',
  person: 'team',
};
